import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Contract, Lane, Regulation, Spot, Violation } from 'src/app/core/models';
import { ContractService, LaneService, RegulationService, SpotService, ViolationService } from 'src/app/core/services';
import { InfraLiveSocketService } from 'src/app/core/services/infraliveSocket.service';
import { InfraliveFilters } from './filters/filters.component';
import { map } from 'rxjs/operators';
import { MediaZipService } from 'src/app/core/services/media-zip.service';
import { Gb } from 'src/app/core/services/growthbook.service';

@Component({
  selector: 'app-infra-live',
  templateUrl: './infra-live.component.html',
  styleUrls: ['./infra-live.component.sass'],
})
export class InfraLiveComponent implements OnInit, OnDestroy {

  public lastData: Violation;
  public connected = false;
  public violations: Violation[] = [];
  public violationData: Array<{ passId: string; spot: Spot; lane: Lane; regulation: Regulation }> = [];
  public loadingList = false;
  public viewFilters = false;
  public contractId: number;
  public contract: Contract;
  public filterParams: InfraliveFilters;
  public fixedLastData = false;
  public paused = false;
  public closedPanel = false;
  public currentFileView = 0;
  public currentViolationView = 0;
  public downloadFilesLoading = false;
  public loadingSpotLane = false;
  public gb: Gb;

  private subscription!: Subscription;
  private subs: Subscription[] = [];
  private laneSpotMap: { [laneCode: string]: string } = {};

  constructor(
    public activatedRoute: ActivatedRoute,
    private infraLive: InfraLiveSocketService,
    private laneService: LaneService,
    private route: ActivatedRoute,
    private spotService: SpotService,
    private regulationService: RegulationService,
    private contractService: ContractService,
    private zipService: MediaZipService,
    private violationService: ViolationService
  ) { }

  ngOnInit(): void {
    this.gb = new Gb();
    this.contractId = this.activatedRoute.snapshot.params.contractId;

    this.getContract();
    this.buildLaneSpotMap();
    this.socketConnect();
    this.loadInitialViolations();

    document.addEventListener('processViolationsReceived', (data: CustomEvent) => this.processViolationsReceived(data));
    document.addEventListener('selectedViolation', (data: CustomEvent) => this.currentFileView = 0);
    document.addEventListener('openOrCloseFilters', (data: CustomEvent) => this.viewFilters = data.detail.viewFilters);
  }

  socketConnect() {
    const interval = setInterval(() => {
      if (this.gb?.isOn('ECS-5820')) {
        return clearInterval(interval);
      }

      if (this.connected) {
        return clearInterval(interval);
      }

      this.infraLive.connect();
      this.infraLive.joinRoom('infralive');
      this.subs.push(
        this.infraLive.isConnected().subscribe((status) => {
          this.connected = status;
        }),
      );

      this.setViolations();
    }, 2500);
  }

  async getContract() {
    this.contract = await this.contractService.getById(this.contractId);
  }

  async buildLaneSpotMap() {
    try {
      const contractId = this.route.snapshot.params.contractId;
      const [lanesResponse, spotsResponse] = await Promise.all([
        this.laneService.list({ contractId }),
        this.spotService.list({ contractId })
      ]);
      for (const lane of lanesResponse.result) {
        const spot = spotsResponse.result.find(s => s.id === lane.spotId);
        if (spot) {
          this.laneSpotMap[lane.code] = spot.code;
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async getLaneByCode(passId: string, code: string, number, regulationId) {
    this.loadingSpotLane = true;
    try {
      const contractId = this.route.snapshot.params.contractId;
      const response = await this.laneService.list({ code, contractId, number });
      let lane: Lane = null;
      let spot: Spot = null;
      if (response.result.length > 0) {
        lane = response.result[0];
        spot = await this.getSpotById(lane.spotId);
        if (spot?.code) {
          this.laneSpotMap[code] = spot.code;
        }
      }
      const regulation = await this.getRegulationById(regulationId);
      this.violationData.push({ passId, spot, lane, regulation });
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingSpotLane = false;
    }
  }

  async getSpotById(id) {
    try {
      return await this.spotService.getById(id);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getRegulationById(id) {
    try {
      return await this.regulationService.getById(id);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  setViolations() {
    this.subscription = this.infraLive.onInfraUpdate()
      .pipe(
        map(data => {
          const violation = (typeof data === 'string' ? JSON.parse(data) : data);
          if (this.paused || Number(violation.contractId) !== Number(this.contractId)) {
            return;
          }
          const event = new CustomEvent('processViolationsReceived', { detail: violation });
          document.dispatchEvent(event);
        })
      )
      .subscribe();
  }

  processViolationsReceived(data: CustomEvent) {
    this.currentFileView = 0;
    const violation = data.detail;
    const index = this.violations.findIndex(v => v.passId === violation.passId);

    if (this.verifyFilters(violation)) {

      const zoomSort = (a, b) => {
        if (a.type === 'zoom') { return -1; }
        if (b.type === 'zoom') { return 1; }
        return 0;
      };

      if (index !== -1) {
        this.violations[index].files = violation.files;
        this.violations[index].files?.sort(zoomSort);
      } else {
        violation.files?.sort(zoomSort);
        this.violations.push(violation);
      }

      if (!this.fixedLastData && index === -1) {
        this.lastData = violation;
        this.currentViolationView = 0;
      }

      this.violations.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      const filters = localStorage.getItem('infraliveFilters') || '{}';
      const filtersData = JSON.parse(filters);
      if (this.violations.length > filtersData?.registerAmount || 10) {
        this.violations = this.violations.slice(0, filtersData?.registerAmount || 10);
      }

      this.getLaneByCode(
        violation.passId,
        violation.laneCode,
        violation.laneNumber,
        violation.regulationId
      );

    }
  }

  async loadInitialViolations() {
    this.loadingList = true;
    try {
      const filters = JSON.parse(localStorage.getItem('infraliveFilters') || '{}');
      const limit = filters.registerAmount || 10;
      const params: any = {
        contractId: this.contractId,
        limit: String(limit),
        order: 'date',
        sortDesc: 'true',
      };
      if (filters.regulations?.length) {
        params['regulationId[in]'] = `[${filters.regulations}]`;
      }
      if (filters.spots?.length) {
        params['spotCode[in]'] = `[${filters.spots}]`;
      }
      if (filters.lanes?.length) {
        params['laneCode[in]'] = `[${filters.lanes}]`;
      }

      const response = await this.violationService.list(params);
      let results = response.result;

      if (!filters.enableNotPlate && filters.enableNotPlate !== undefined) {
        results = results.filter(v => !!v.vehicle?.plate?.trim());
      }
      if (filters.classification?.length) {
        const classIds = filters.classification.map(Number);
        results = results.filter(v => classIds.includes(Number(v.vehicle?.classificationCode)));
      }

      results.forEach(v => v.files?.sort((a, b) => {
        if (a.type === 'zoom') { return -1; }
        if (b.type === 'zoom') { return 1; }
        return 0;
      }));

      this.violations = results;
      this.violations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      if (this.violations.length > 0) {
        this.lastData = this.violations[0];
      }

      for (const v of this.violations) {
        this.getLaneByCode(v.passId, v.laneCode, v.laneNumber, v.regulationId);
      }
    } catch (error) {
      console.error('Erro ao carregar infrações iniciais:', error);
    } finally {
      this.loadingList = false;
    }
  }

  setFilters(f: InfraliveFilters, onpenOrCLoseFilters = true) {
    if (onpenOrCLoseFilters) {
      this.openOrCloseFilters();
    }
    this.violations = [];
    this.violationData = [];
    this.lastData = undefined;
    this.currentViolationView = 0;
    this.currentFileView = 0;
    this.loadInitialViolations();
  }

  verifyFilters(violation: Violation): boolean {
    const raw = localStorage.getItem('infraliveFilters');
    if (!raw) { return true; }

    const filters = JSON.parse(raw);

    if (!filters) { return true; }

    const hasPlate = !!violation?.vehicle?.plate?.trim();

    if (!filters.enableNotPlate && !hasPlate) {
      return false;
    }

    const matchArray = (filterArray: any[], value: any) => {
      if (!Array.isArray(filterArray) || filterArray.length === 0) { return true; }
      return filterArray.map(String).includes(String(value));
    };

    if (!matchArray(filters.regulations, violation.regulationId)) { return false; }
    const spotCode = violation.spotCode || this.laneSpotMap[violation.laneCode];
    if (!matchArray(filters.spots, spotCode)) { return false; }
    if (!matchArray(filters.lanes, violation.laneCode)) { return false; }
    if (!matchArray(filters.classification, violation.vehicle?.classificationCode)) { return false; }

    return true;
  }

  getRegulationByPassId(passId: string) {
    const data = this.violationData.find(a => a.passId === passId);
    if (data === undefined) {
      return undefined;
    }
    return data?.regulation;
  }

  getSpotByPassId(passId) {
    const spot = this.violationData.find(a => a.passId === passId)?.spot?.description;
    return spot || '-';
  }

  openOrCloseFilters() {
    this.viewFilters = !this.viewFilters;
    const event = new CustomEvent('openOrCloseFilters', { detail: { viewFilters: this.viewFilters } });
    document.dispatchEvent(event);
  }

  setFixedLastData() {
    if (this.lastData !== undefined) {
      this.fixedLastData = !this.fixedLastData;
    }
  }

  nextFileView() {
    if (this.lastData && this.lastData.files.length > 0) {
      this.currentFileView = (this.currentFileView + 1) % this.lastData.files.length;
    }
  }

  previousFileView() {
    if (this.lastData && this.lastData.files.length > 0) {
      this.currentFileView =
        (this.currentFileView - 1 + this.lastData.files.length) % this.lastData.files.length;
    }
  }

  updateViolation() {
    this.lastData = this.violations[this.currentViolationView] ?? null;
  }

  prevViolation() {
    if (this.violations.length === 0) {
      return;
    }
    this.currentViolationView = Math.max(0, this.currentViolationView - 1);
    this.updateViolation();
  }

  nextViolation() {
    if (this.violations.length === 0) {
      return;
    }
    this.currentViolationView = Math.min(
      this.violations.length - 1,
      this.currentViolationView + 1
    );
    this.updateViolation();
  }

  getRegulationColor(regulationId: number) {
    const target = this.contract?.ContractConfigColors?.find(a => a.regulationId === regulationId);
    if (target !== undefined) {
      return target.color;
    }
    return '';
  }

  async downloadFiles() {
    if (this.lastData !== undefined) {
      this.downloadFilesLoading = true;
      this.fixedLastData = true;
      await this.zipService.downloadZip(this.lastData);
      this.downloadFilesLoading = false;
      this.fixedLastData = false;
    }
  }

  openPanel() {
    this.closedPanel = false;
    const event = new CustomEvent('openPanel', { detail: true });
    document.dispatchEvent(event);
  }

  ngOnDestroy(): void {
    localStorage.removeItem('infraliveFilters');
    this.subs.forEach((s) => s.unsubscribe());
    this.subscription?.unsubscribe();
    this.infraLive.disconnect();
  }
}
